import { useTranslations } from '@vocab/react';
import {
  Text,
  Badge,
  Stack,
  Box,
  Inline,
  TooltipRenderer,
  IconEdit,
  IconRefresh,
  IconCopy,
  IconNewWindow,
  IconRenderer,
} from 'braid-design-system';
import type { FC, PropsWithChildren } from 'react';

import { OverflowMenu } from 'src/App/Components/OverflowMenu/OverflowMenu';
import { EmptyTableCell } from 'src/App/Components/RecentJobs/EmptyTableCell';
import TrackingButtonLink from 'src/App/Components/TrackingButtonLink/TrackingButtonLink';
import { getTrackingMenuItem } from 'src/App/Components/TrackingMenuItem/TrackingMenuItem';
import TrackingTextLink from 'src/App/Components/TrackingTextLink/TrackingTextLink';
import { analyticsEvents } from 'src/constants/analytics';
import {
  TRACKING_DASHBOARD_JOB_LIST,
  TRACKING_RECENT_JOBS,
} from 'src/constants/tracking';
import {
  type DashboardDraftFragment,
  type DashboardJobFragment,
  type PermissionOptions,
  JobStatus as Status,
  DraftStatus,
} from 'src/graphql/generated';
import { trackPageInteraction } from 'src/modules/AnalyticsTracker';
import { useTalentSearchContext } from 'src/modules/ApiClient';
import type {
  AnalyticsTrackingData,
  DashboardListItemFragment,
} from 'src/types';
import { urlResolver } from 'src/utils/config';
import {
  getCandidateManagementUrl,
  getContinueDraftUrl,
  getCopyJobUrl,
  getEditJobUrl,
  getRecommendedCandidatesUrl,
  getRefreshJobUrl,
  getUpgradeJobUrl,
  getViewJobAdUrl,
} from 'src/utils/getUrls';
import {
  getJobKey,
  getJobTitleLink,
  getLocationDescription,
  isActiveOrExpiredJob,
  isDraftJobItem,
  isPositiveNumber,
  isIndirectJob,
  getTitle,
} from 'src/utils/job';

import translations from './.vocab';
import { IconRocket } from './IconRocket';

export interface JobFieldProps {
  job: DashboardListItemFragment;
  listPosition?: number;
}

interface JobFieldPermissionsProps {
  permissions: PermissionOptions;
}

export const getJobTrackingData = ({
  job,
  listPosition,
}: {
  job: DashboardJobFragment | DashboardDraftFragment;
  listPosition?: number;
}): AnalyticsTrackingData => {
  if (isDraftJobItem(job)) {
    return {
      adDraftId: job.id,
      adPosition: listPosition,
      candidateCount: 0,
      entryPoint: 'dashboard job list',
    };
  }

  return {
    seekListingId: job.id,
    adPosition: listPosition,
    candidateCount: job.candidatesCount || 0,
    adHealth: job.performanceRating?.score,
    entryPoint: 'dashboard job list',
  };
};

export interface DraftJobStatusProps {
  isDraft: true;
  loading?: boolean;
  status?: string;
}

export interface PostedJobStatusProps {
  jobId: string;
  isDraft: false;
  daysRemaining?: number | null;
  status?: Status;
}

export type Props = DraftJobStatusProps | PostedJobStatusProps;

export const JobStatus: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { t } = useTranslations(translations);

  if (props.isDraft) {
    const isPendingDraft = props.status === DraftStatus.PENDING;
    return (
      <Badge tone={isPendingDraft ? 'positive' : 'caution'}>
        {isPendingDraft ? t('Pending') : t('Draft')}
      </Badge>
    );
  }

  const { status, daysRemaining, jobId } = props;

  switch (status) {
    case Status.EXPIRED:
      return <Badge tone="neutral">{t('Expired')}</Badge>;
    case Status.BLOCKED:
      return (
        <Inline space="small">
          <TooltipRenderer
            id={`tooltip-${jobId}`}
            tooltip={<Text size="small">{t('Blocked tooltip')}</Text>}
          >
            {({ triggerProps }) => (
              <Box aria-label="Help" {...triggerProps}>
                <Box pointerEvents="none">
                  <Badge tone="critical">{t('Blocked')}</Badge>
                </Box>
              </Box>
            )}
          </TooltipRenderer>
        </Inline>
      );
    case Status.PENDING:
    case Status.PENDING_ACCOUNT_APPROVAL:
    case Status.SUSPENDED:
      return <Badge tone="caution">{t('Pending')}</Badge>;
    case Status.ACTIVE:
      if (!daysRemaining) {
        return <Badge tone="info">{t('Active')}</Badge>;
      }
      return <Text size="small">{t('days left', { daysRemaining })}</Text>;
    default:
      return null;
  }
};

export interface JobTitleAndLocationProps {
  job: DashboardListItemFragment;
  maxLines: number;
  listPosition?: number;
}

export const JobTitleAndLocation: FC<
  PropsWithChildren<JobTitleAndLocationProps>
> = ({ job, maxLines, listPosition }) => {
  const { t } = useTranslations(translations);

  const { href, target } = getJobTitleLink(job);

  const location = getLocationDescription(job);
  const jobTitle = getTitle(job) ?? t('Untitled');

  return (
    <Stack space="small">
      <Stack space="xsmall">
        <TrackingTextLink
          href={href}
          target={target}
          trackingEventName={analyticsEvents.TRACK_JOB_TITLE}
          trackingActionOrigin={TRACKING_DASHBOARD_JOB_LIST}
          trackingData={getJobTrackingData({ job, listPosition })}
        >
          <Text maxLines={maxLines ? maxLines : 1} weight="strong" size="small">
            {jobTitle}
          </Text>
        </TrackingTextLink>

        {Boolean(location) && (
          <Text data={{ testid: 'location' }} size="small" maxLines={maxLines}>
            {location}
          </Text>
        )}
      </Stack>

      {isIndirectJob(job) && <Badge tone="neutral">{t('Uploaded')}</Badge>}
    </Stack>
  );
};

export const JobNumOfCandidates = ({ job, listPosition }: JobFieldProps) => {
  const { t } = useTranslations(translations);

  if (
    isDraftJobItem(job) ||
    !isActiveOrExpiredJob(job) ||
    !isPositiveNumber(job.candidatesCount)
  ) {
    return <EmptyTableCell />;
  }

  return (
    <Inline space="xxsmall" alignY="center">
      <TrackingTextLink
        href={getCandidateManagementUrl(job.id)}
        trackingEventName={analyticsEvents.TRACK_VIEW_CANDIDATES_COUNT}
        trackingActionOrigin={TRACKING_DASHBOARD_JOB_LIST}
        trackingData={getJobTrackingData({ job, listPosition })}
      >
        <Text size="small">{job.candidatesCount}</Text>
      </TrackingTextLink>
      {isPositiveNumber(job.newCandidatesCount) && (
        <Badge tone="positive">
          {job.newCandidatesCount} {t('New')}
        </Badge>
      )}
    </Inline>
  );
};

export const JobNumOfCandidatesMobile = ({
  job,
  listPosition,
}: {
  job: DashboardJobFragment;
  listPosition?: number;
}) => {
  const { t } = useTranslations(translations);

  return (
    <Inline space="small" alignY="center">
      <TrackingTextLink
        href={getCandidateManagementUrl(job.id)}
        trackingEventName={analyticsEvents.TRACK_VIEW_CANDIDATES_COUNT}
        trackingActionOrigin={TRACKING_RECENT_JOBS}
        trackingData={getJobTrackingData({ job, listPosition })}
      >
        <Text>{job.candidatesCount ?? 0}</Text>
      </TrackingTextLink>
      {isPositiveNumber(job.newCandidatesCount) && (
        <Badge tone="positive">
          {job.newCandidatesCount} {t('New')}
        </Badge>
      )}
    </Inline>
  );
};

export const JobTalentSearch = ({ job, listPosition }: JobFieldProps) => {
  const { data: tsContext } = useTalentSearchContext();
  const { t } = useTranslations(translations);

  if (isDraftJobItem(job) || job.status !== 'ACTIVE') {
    return <EmptyTableCell />;
  }

  if (!tsContext) {
    return null;
  }

  return (
    <TrackingTextLink
      href={urlResolver({
        path: getRecommendedCandidatesUrl(job),
      })}
      target="_blank"
      trackingEventName={analyticsEvents.TRACK_CONTENT_ELEMENT}
      trackingActionOrigin={TRACKING_DASHBOARD_JOB_LIST}
      trackingData={getJobTrackingData({ job, listPosition })}
    >
      <Text size="small">{t('Talent Search link')}</Text>
    </TrackingTextLink>
  );
};

interface JobActionButtonLinkProps {
  label: string;
  url: string;
  job: DashboardListItemFragment;
  listPosition?: number;
  target?: string;
  trackingEventName: string;
  trackingActionOrigin?: string;
}

const JobActionButtonLink = ({
  label,
  url,
  target,
  job,
  listPosition,
  trackingEventName,
  trackingActionOrigin,
}: JobActionButtonLinkProps) => (
  <TrackingButtonLink
    variant="ghost"
    size="small"
    href={url}
    target={target}
    trackingEventName={trackingEventName}
    trackingActionOrigin={
      trackingActionOrigin ? trackingActionOrigin : TRACKING_RECENT_JOBS
    }
    trackingData={getJobTrackingData({ job, listPosition })}
  >
    {label}
  </TrackingButtonLink>
);

export const JobActions = ({
  job,
  listPosition,
  permissions,
}: JobFieldProps & JobFieldPermissionsProps) => {
  if (isDraftJobItem(job)) {
    return (
      <JobActionsDrafts
        job={job}
        permissions={permissions}
        listPosition={listPosition}
      />
    );
  }

  return (
    <JobActionsJobs
      job={job}
      permissions={permissions}
      listPosition={listPosition}
    />
  );
};

export const JobActionsJobs = ({
  job,
  listPosition,
  permissions,
}: {
  job: DashboardJobFragment;
  listPosition?: number;
} & JobFieldPermissionsProps) => {
  const { t } = useTranslations(translations);

  const canUpgradeJob =
    job.canBeUpgraded && permissions.canCreateJobs && job.status !== 'EXPIRED';

  if (canUpgradeJob) {
    return <JobUpgradeButtonLink job={job} />;
  }

  if (job.status === 'PENDING') {
    return null;
  }

  const { status, id } = job;
  const seeApplicantsButton = id && (
    <JobActionButtonLink
      label={t('See applicants button')}
      url={getCandidateManagementUrl(id)}
      job={job}
      listPosition={listPosition}
      trackingEventName={analyticsEvents.TRACK_VIEW_APPLICANTS}
      trackingActionOrigin={TRACKING_DASHBOARD_JOB_LIST}
    />
  );

  switch (status) {
    case 'ACTIVE':
      return seeApplicantsButton;
    case 'EXPIRED':
      return permissions.canCreateJobs && id ? (
        <JobActionButtonLink
          label={t('Copy button')}
          url={getCopyJobUrl(id)}
          job={job}
          listPosition={listPosition}
          trackingEventName={analyticsEvents.TRACK_COPY_JOB}
        />
      ) : (
        seeApplicantsButton
      );
  }

  return null;
};

export const JobActionsDrafts = ({
  job,
  listPosition,
  permissions,
}: {
  job: DashboardDraftFragment;
  listPosition?: number;
} & JobFieldPermissionsProps) => {
  const { t } = useTranslations(translations);

  if (!permissions.canCreateJobs || !job.id) {
    return null;
  }

  return (
    <JobActionButtonLink
      label={t('Continue draft button')}
      url={getContinueDraftUrl(job.id)}
      job={job}
      listPosition={listPosition}
      trackingEventName={analyticsEvents.TRACK_CONTINUE_DRAFT}
      trackingActionOrigin={TRACKING_DASHBOARD_JOB_LIST}
    />
  );
};

export const JobOverflowMenu = ({
  job,
  listPosition,
  permissions,
}: {
  job: DashboardJobFragment;
  listPosition?: number;
} & JobFieldPermissionsProps) => {
  const { t } = useTranslations(translations);
  const { status } = job;
  const { canCreateJobs } = permissions;
  const options: JSX.Element[] = [];

  if (!canCreateJobs || status === 'BLOCKED') {
    return null;
  }
  const trackingData = getJobTrackingData({ job, listPosition });
  const { href, target } = getViewJobAdUrl(job.id, status!);

  switch (status) {
    case 'ACTIVE': {
      if (job.postType === 'DIRECT') {
        options.push(
          getTrackingMenuItem({
            label: t('Edit button'),
            key: getJobKey(job),
            href: getEditJobUrl(job.id),
            icon: <IconEdit />,
            trackingEventName: analyticsEvents.TRACK_EDIT_JOB,
            trackingActionOrigin: TRACKING_DASHBOARD_JOB_LIST,
            trackingData,
          }),
        );
      }

      if (job.id) {
        options.push(
          getTrackingMenuItem({
            label: t('Copy button'),
            key: getJobKey(job),
            href: getCopyJobUrl(job.id),
            icon: <IconCopy />,
            trackingEventName: analyticsEvents.TRACK_COPY_JOB,
            trackingActionOrigin: TRACKING_DASHBOARD_JOB_LIST,
            trackingData,
          }),
        );
      }

      options.push(
        getTrackingMenuItem({
          label: t('View job ad button'),
          target,
          key: getJobKey(job),
          href,
          icon: <IconNewWindow />,
          trackingEventName: analyticsEvents.TRACK_VIEW_JOB,
          trackingActionOrigin: TRACKING_DASHBOARD_JOB_LIST,
          trackingData,
        }),
      );
      if (job.canBeRefreshed) {
        options.push(
          getTrackingMenuItem({
            label: t('Refresh button'),
            key: getJobKey(job),
            href: getRefreshJobUrl(job.id),
            icon: <IconRefresh />,
            trackingEventName: analyticsEvents.TRACK_REFRESH_JOB,
            trackingActionOrigin: TRACKING_DASHBOARD_JOB_LIST,
            trackingData,
          }),
        );
      }
      break;
    }

    case 'EXPIRED': {
      options.push(
        getTrackingMenuItem({
          label: t('View job ad button'),
          target,
          key: getJobKey(job),
          href,
          icon: <IconNewWindow />,
          trackingEventName: analyticsEvents.TRACK_VIEW_JOB,
          trackingActionOrigin: TRACKING_DASHBOARD_JOB_LIST,
          trackingData,
        }),
      );
      break;
    }
  }

  return options.length ? (
    <Box display="flex" alignItems="center" justifyContent="flexEnd">
      <OverflowMenu
        label={t('Options button')}
        menuWidth={145}
        onOpen={() => {
          trackPageInteraction(
            analyticsEvents.TRACK_MORE_OPTIONS,
            TRACKING_DASHBOARD_JOB_LIST,
            getJobTrackingData({ job, listPosition }),
          );
        }}
      >
        {options}
      </OverflowMenu>
    </Box>
  ) : null;
};

export const JobUpgradeButtonLink = ({
  job,
}: {
  job: DashboardJobFragment;
}) => {
  const { t } = useTranslations(translations);
  return (
    <TrackingButtonLink
      href={getUpgradeJobUrl(job.id)}
      icon={
        <IconRenderer>
          {({ className }) => <IconRocket className={className} />}
        </IconRenderer>
      }
      iconPosition="trailing"
      size="small"
      tone="brandAccent"
      trackingEventName={analyticsEvents.TRACK_UPGRADE_JOB_CTA}
      trackingActionOrigin={TRACKING_DASHBOARD_JOB_LIST}
      trackingData={{
        jobId: job.id,
      }}
    >
      {t('Upgrade')}
    </TrackingButtonLink>
  );
};
